﻿import { ProductViewModelFactory, ProductViewModelFactories } from './ProductViewModelFactories.js';
import { DoorFrontEditor } from './DoorFrontEditor.js';
import { ProductEditor } from './ProductEditor.js';
import {
  SideWallEditor, FlexiSideEditor, FlexiShelfEditor, FlexiBasketAndTracksEditor,
  MeshBasketAndTracksEditor, FlexiSectionEditor, ClosetOrganizerEditor
} from './InteriorEditor.js';
import { SingleVariantProductEditor, MultiVariantProductEditor } from './VariantProductEditor.js';
import { DoorFront, SideWall, FixedProduct, OneDimVariantProduct, MultiDimVariantProduct } from './Components.js';
import {
  FlexiSide, HangRodWithBrackets, SupportRodWithBrackets, FlexiBasketAndTracks, MeshBasketAndTracks,
  TopShelf, FlexiShelf, FlexiSection, TopShelfModule, HangRodModule, ClosetOrganizer
} from './Components-Flexi.js';

const PriceMarketCodes = {
  EuCm: 'eu-cm', // Euro-zone, consumer market.
  NoRc: 'no-rc', // Norway, retail chain market
  NoCm: 'no-cm', // Norway, consumer market
}

class SlidingDoorFrontFactory extends ProductViewModelFactory {

  /**
   * This method might--if session storage contains a product--communicate with the remote
   * designerService to asynchronously load the product. The async load happens on the last line of
   * the ProductEditor class constructor.
   */
  createProductEditor(productEditorConfig, designerService, productService) {
    return new DoorFrontEditor(productEditorConfig, designerService, productService);
  }

  getAllKnownSafirMaterialIds(mktCode) {
    // TODO: Allow the returned material id's to vary based on mktCode. Currently this returns known materials for the EuCm market
    const materialIds = {};
    const frameFinishes = ['WhiteAluminum', 'NaturalAluminum', 'BlackAluminum'];
    const doorPanelFinishes = [
      'WhiteMelamine',
      'ClearMirror',
      'GrayMirror',
      'BronzeMirror',
      'WhiteGlass',
      'OakVeneer',
      'BirchVeneer',
      'GrayReed',
      'LightGrayReed',
      'OakReed',
      'WhiteMelamineReed',
      'BlackOakPaint',
      'StoneAsh',
      'LavaElm',
      'Taupe_NCS_S6005_Y20R',
      'Black_NCS_S9000N',
      'AntrasiteMelamine',
      'ManhattanGrayMelamine',
    ];
    const trackFinishes = ['WhiteAluminum', 'SilverAluminum', 'BlackAluminum'];

    this.addMaterials(materialIds, 'AluFlatDivider', frameFinishes);
    this.addMaterials(materialIds, 'AluHDivider', frameFinishes);
    this.addMaterials(materialIds, 'SafirBottomDoorRail', frameFinishes);
    this.addMaterials(materialIds, 'SafirTopDoorRail', frameFinishes);
    this.addMaterials(materialIds, 'SafirDoorStile', frameFinishes);
    this.addMaterials(materialIds, 'DoorPanel', doorPanelFinishes);
    this.addMaterials(materialIds, 'SlidingDoorTopTrack-Safir', trackFinishes);
    this.addMaterials(materialIds, 'SlidingDoorBottomTrack-Safir', trackFinishes);

    return materialIds;
  }

  getAllKnownTopazMaterialIds(mktCode) {
    // TODO: Allow the returned material id's to vary based on mktCode. Currently this returns known materials for the EuCm market
    const materialIds = {};
    const frameFinishes = ['OakVeneer', 'WhiteNCS_S0500N', 'GrayNCS_S5500N', 'BlackOakPaint'];
    const doorPanelFinishes = [
      'ClearMirror',
      'GrayMirror',
      'BronzeMirror',
      'WhiteGlass',
      'OakVeneer',
      'WhiteMelamineReed',
      'BlackOakPaint',
      'WhiteNCS_S0500N',
      'GrayNCS_S5500N',
    ];
    const trackFinishes = ['WhiteAluminum', 'SilverAluminum', 'BlackAluminum'];

    this.addMaterials(materialIds, 'DoorPanel', doorPanelFinishes);
    this.addMaterials(materialIds, 'TopazDoorRail', frameFinishes);
    this.addMaterials(materialIds, 'TopazDoorStile', frameFinishes);
    this.addMaterials(materialIds, 'TopazHDivider', frameFinishes);
    // Note, Topaz uses the same track types (they're called Safir tracks)
    this.addMaterials(materialIds, 'SlidingDoorTopTrack-Safir', trackFinishes);
    this.addMaterials(materialIds, 'SlidingDoorBottomTrack-Safir', trackFinishes);

    return materialIds;
  }

  getAllKnownEdgeMaterialIds(mktCode) {
    // TODO: Allow the returned material id's to vary based on mktCode. Currently this returns known Safir materials for the EuCm market
    const materialIds = {};
    const frameFinishes = ['WhiteAluminum', 'NaturalAluminum', 'BlackAluminum'];
    const doorPanelFinishes = [
      'WhiteMelamine',
      'ClearMirror',
      'GrayMirror',
      'BlackGlass',
      'GrayGlass',
      'WhiteGlass',
      'OakVeneer',
      'BirchVeneer',
      'GrayReed',
      'LightGrayReed',
      'OakReed',
      'WhiteMelamineReed',
      'BlackOakPaint',
      'StoneAsh',
      'LavaElm',
      'Black_NCS_S9000N',
      'OakWhiteMelamine',
      'ManhattanGrayMelamine',
    ];
    const trackFinishes = ['WhiteAluminum', 'SilverAluminum', 'BlackAluminum'];

    this.addMaterials(materialIds, 'DoorPanel', doorPanelFinishes);
    this.addMaterials(materialIds, 'EdgeHDivider', frameFinishes);
    this.addMaterials(materialIds, 'EdgeDoorRail', frameFinishes);
    this.addMaterials(materialIds, 'EdgeDoorStile', frameFinishes);
    // Note, Edge uses the same track types (they're called Safir tracks)
    this.addMaterials(materialIds, 'SlidingDoorTopTrack-Safir', trackFinishes);
    this.addMaterials(materialIds, 'SlidingDoorBottomTrack-Safir', trackFinishes);

    return materialIds;
  }

  getAllKnownProductMaterialIds(apiProduct) {
    if (apiProduct.doors.length > 0) {
      switch (apiProduct.doors[0].design) {
        case 'Safir':
          return this.getAllKnownSafirMaterialIds(apiProduct.mktCode);
        case 'Topaz':
          return this.getAllKnownTopazMaterialIds(apiProduct.mktCode);
        case 'Edge':
          return this.getAllKnownEdgeMaterialIds(apiProduct.mktCode);
        default:
      }
    } else {
      return [];
    }
  }

  createProduct(drawingService, apiProduct, config) {
    return new DoorFront('Door Front', drawingService, apiProduct, config);
  }
}

/** ProductViewModelFactory which has a productClass property used when instantiating a product. */
class ProductClassFactory extends ProductViewModelFactory {
  constructor(productType, productClass) {
    super(productType);
    this.productClass = productClass;
  }

  createProduct(drawingService, apiProduct, config) {
    return new this.productClass(this.productType, drawingService, apiProduct, config);
  }
}

class SideWallFactory extends ProductClassFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new SideWallEditor(productEditorConfig, designerService, productService);
  }

  getAllKnownProductMaterialIds(apiProduct) {
    const materialIds = {};
    /* NOTE: If a used finish is not listed here, then the ui rendering (e.g. HtmlRenderVisitor) will not
     * be able to display the material. In HtmlRenderVisitor's case a warning will be displayed in the
     * console. */
    const sideWallFinishes = [
      'BirchVeneer',
      'OakVeneer',
      'OakWhiteMelamine',
      'PrimedFoil',
      'WhiteMelamine',
      'ManhattanGrayMelamine',
      'VulcanGrayMelamine',
      'SanRemoSandOak',
      'Black_NCS_S9000N',
      'StoneAsh',
      'Taupe_NCS_S6005_Y20R'
    ];
    this.addMaterials(materialIds, 'SideWall', sideWallFinishes);
    // Not all sidewall specific materials might exist. Let's include generic material names as well:
    sideWallFinishes.forEach(finish => materialIds[finish] = true);
    return materialIds;
  }
}

class InteriorFactory extends ProductClassFactory {
  getPrimaryFinishes() {
    return [
      'OakWhiteMelamine',
      'WhiteMelamine',
      'Black_NCS_S9000N',
    ];
  }

  getMetalFinishes() {
    return [
      'WhiteSteel',
      'SilverSteel',
      'BlackSteel',
    ];
  }

  /** returns an object with properties matching the finishes identified by the finishes parameter. */
  addToProductMaterialIds(materialIds, finishes, prefix) {
    // Add the finishes array values as properties of the materialIds object:
    this.addMaterials(materialIds, prefix, finishes);
    // Not all specific materials might exist. Let's add properties for the finishes without prefix as well:
    finishes.forEach(finish => materialIds[finish] = true);
    return materialIds;
  }

  /** Adds the finishes identified by the getPrimaryFinishes() method. */
  addPrimaryProductMaterialIds(materialIds, prefix) {
    const finishes = this.getPrimaryFinishes();
    this.addToProductMaterialIds(materialIds, finishes, prefix);
  }

  /** Adds the finishes identified by the getMetalFinishes() method. */
  addMetalProductMaterialIds(materialIds, prefix) {
    const finishes = this.getMetalFinishes();
    this.addToProductMaterialIds(materialIds, finishes, prefix);
  }

  /** returns an object with properties matching the finishes identified by the getPrimaryFinishes() method. */
  getPrimaryProductMaterialIds(prefix) {
    const materialIds = {};
    // Add the finishes array values as properties of the materialIds object:
    this.addPrimaryProductMaterialIds(materialIds, prefix);
    return materialIds;
  }

  /** returns an object with properties matching the finishes identified by the getMetalFinishes() method. */
  getMetalProductMaterialIds(prefix) {
    const materialIds = {};
    // Add the finishes array values as properties of the materialIds object:
    this.addMetalProductMaterialIds(materialIds, prefix);
    return materialIds;
  }
}

class FlexiSideFactory extends InteriorFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new FlexiSideEditor(productEditorConfig, designerService, productService);
  }

  getAllKnownProductMaterialIds(apiProduct) {
    /* NOTE: If a used finish is not listed here, then the ui rendering (e.g. HtmlRenderVisitor) will not
     * be able to display the material. In HtmlRenderVisitor's case a warning will be displayed in the
     * console. */
    return this.getPrimaryProductMaterialIds('FlexiSide');
  }
}

class FlexiShelfFactory extends InteriorFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new FlexiShelfEditor(productEditorConfig, designerService, productService);
  }

  getAllKnownProductMaterialIds(apiProduct) {
    /* NOTE: If a used finish is not listed here, then the ui rendering (e.g. HtmlRenderVisitor) will
     * not be able to display the material. In HtmlRenderVisitor's case a warning will be displayed
     * in the console. */
    const materialIds = {}
    this.addPrimaryProductMaterialIds(materialIds, 'FlexiShelf');
    this.addMetalProductMaterialIds(materialIds, 'FlexiShelf');
    return materialIds;
  }
}

class FlexiBasketAndTracksFactory extends InteriorFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new FlexiBasketAndTracksEditor(productEditorConfig, designerService, productService);
  }

  getAllKnownProductMaterialIds(apiProduct) {
    /* NOTE: If a used finish is not listed here, then the ui rendering (e.g. HtmlRenderVisitor) will
     * not be able to display the material. In HtmlRenderVisitor's case a warning will be displayed
     * in the console. */
    return this.getMetalProductMaterialIds('FlexiBasketAndTracks');
  }
}

class MeshBasketAndTracksFactory extends InteriorFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new MeshBasketAndTracksEditor(productEditorConfig, designerService, productService);
  }

  getAllKnownProductMaterialIds(apiProduct) {
    /* NOTE: If a used finish is not listed here, then the ui rendering (e.g. HtmlRenderVisitor) will
     * not be able to display the material. In HtmlRenderVisitor's case a warning will be displayed
     * in the console. */
    return this.getMetalProductMaterialIds('MeshBasketAndTracks');
  }
}

class FloorModuleFactory extends InteriorFactory {
  getAllKnownProductMaterialIds(apiProduct) {
    const knownPrimaryPrefixes = [
      'FlexiShelf',
      'FlexiSide',
    ];
    const knownMetalPrefixes = [
      'FlexiShelf',
      'FlexiBasketAndTracks',
      'MeshBasketAndTracks',
      'HangRodWithBrackets',
      'SupportRodWithBrackets',
    ];
    /* NOTE: If a used finish is not listed here, then the ui rendering (e.g. HtmlRenderVisitor)
     * will not be able to display the material. In HtmlRenderVisitor's case a warning will be
     * displayed in the console. */
    const materialIds = {}
    knownPrimaryPrefixes.forEach(prefix => this.addPrimaryProductMaterialIds(materialIds, prefix));
    knownMetalPrefixes.forEach(prefix => this.addMetalProductMaterialIds(materialIds, prefix));
    return materialIds;
  }
}

class FlexiSectionFactory extends FloorModuleFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new FlexiSectionEditor(productEditorConfig, designerService, productService);
  }
}

class ClosetOrganizerFactory extends FloorModuleFactory {
  createProductEditor(productEditorConfig, designerService, productService) {
    return new ClosetOrganizerEditor(productEditorConfig, designerService, productService);
  }
}

class FixedDesignerProductFactory extends ProductViewModelFactory {
  constructor(productType) {
    super(productType);
  }

  createProductEditor(productEditorConfig, designerService, productService) {
    return new ProductEditor(productEditorConfig, designerService, productService);
  }
}

class OneDimVariantProductFactory extends ProductViewModelFactory {
  constructor(productType) {
    super(productType);
  }

  createProductEditor(productEditorConfig, designerService, productService) {
    return new SingleVariantProductEditor(productEditorConfig, designerService, productService);
  }
}

class MultiDimVariantProductFactory extends ProductViewModelFactory {
  constructor(productType) {
    super(productType);
  }

  createProductEditor(productEditorConfig, designerService, productService) {
    return new MultiVariantProductEditor(productEditorConfig, designerService, productService);
  }
}

export function RegisterFactories() {
  ProductViewModelFactories.register('SlidingDoorFront', SlidingDoorFrontFactory);
  ProductViewModelFactories.register('SideWall', SideWallFactory, SideWall);
  ProductViewModelFactories.register('FlexiSide', FlexiSideFactory, FlexiSide);
  ProductViewModelFactories.register('HangRodWithBrackets', InteriorFactory, HangRodWithBrackets);
  ProductViewModelFactories.register('SupportRodWithBrackets', InteriorFactory, SupportRodWithBrackets);
  ProductViewModelFactories.register('FlexiBasketAndTracks', FlexiBasketAndTracksFactory, FlexiBasketAndTracks);
  ProductViewModelFactories.register('MeshBasketAndTracks', MeshBasketAndTracksFactory, MeshBasketAndTracks);
  ProductViewModelFactories.register('TopShelf', InteriorFactory, TopShelf);
  ProductViewModelFactories.register('FlexiShelf', FlexiShelfFactory, FlexiShelf);
  ProductViewModelFactories.register('FlexiSection', FlexiSectionFactory, FlexiSection);
  ProductViewModelFactories.register('TopShelfModule', InteriorFactory, TopShelfModule);
  ProductViewModelFactories.register('HangRodModule', InteriorFactory, HangRodModule);
  ProductViewModelFactories.register('ClosetOrganizer', ClosetOrganizerFactory, ClosetOrganizer);
  ProductViewModelFactories.register('FixedDesignerProduct', FixedDesignerProductFactory, FixedProduct);
  ProductViewModelFactories.register('OneDimVariantProduct', OneDimVariantProductFactory, OneDimVariantProduct);
  ProductViewModelFactories.register('MultiDimVariantProduct', MultiDimVariantProductFactory, MultiDimVariantProduct);
}
